import React, { useContext } from "react";
import { Box, Grid, IconButton, Stack } from "@mui/material";
import styled from "styled-components";
import InfoIcon from "@mui/icons-material/Info";
import InvestmentSection from "src/scenes/overview/overview-components/main-sections/investment-section";
import DevCost from "src/scenes/overview/overview-components/main-sections/dev-cost";
import CodeCost from "src/scenes/overview/overview-components/main-sections/code-cost";
import DeliverySectionOutput from "src/scenes/overview/overview-components/main-sections/delivery-section";
import DeliverySectionEfficacy from "src/scenes/overview/overview-components/main-sections/delivery-section-efficacy";
import { OverviewContext } from "src/components/providers/overview-provider";
import OverviewRefreshSection from "src/scenes/overview/overview-components/overview-refresh-section";
import { useActiveDate } from "src/scenes/dashboard/date-provider";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import { useTemplateViewEdit } from "src/components/providers/template-view-edit-provider";
import { startOfDay, subMonths } from "date-fns";
import TemplateTitle from "src/components/template/template-title";
import KnowledgebaseIconLink from "src/components/knowledgebase-icon-link";
import InvalidIntegrationButton from "src/components/invalid-integration-button";
import Template from "src/components/template/template";

const SectionTitle = styled.p`
    font-size: 16px;
    font-weight: 600;
`;

const StyledMenuItem = styled(MenuItem)`
    font-size: 13px;
`;

const OverviewMain = () => {
    const [isInvestmentFlipped, setIsInvestmentFlipped] = React.useState(false);
    const [isDeliveryFlipped, setIsDeliveryFlipped] = React.useState(false);
    const [isExpenditureFlipped, setIsExpenditureFlipped] = React.useState(false);
    const { dashboard } = useTemplateViewEdit();
    const { handleChange: handleDateChange } = useActiveDate();
    const { executiveApi, expandedView, setRefresh, semesters, setSemesters } =
        useContext(OverviewContext);

    const data = executiveApi && executiveApi.data;
    const fetchAction = executiveApi;

    const handleSemestersChange = (event) => {
        const value = parseInt(event.target.value, 10);
        setSemesters(value);
        handleDateChange(subMonths(startOfDay(new Date()), 6 * value), startOfDay(new Date()));
    };

    const renderStyledPeriodValue = () => {
        if (!semesters) return null;
        let semestersItems;
        if (semesters === 1) semestersItems = "6 Months";
        if (semesters === 2) semestersItems = "1 Year";
        if (semesters === 3) semestersItems = "18 Months";
        return (
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    fontSize: "13px",
                    fontWeight: "300",
                }}
            >
                {semestersItems}
            </Box>
        );
    };

    return (
        <>
            <Stack direction="row" justifyContent="end" mt={1}>
                <FormControl>
                    <InputLabel id="period-select-label" size="small">
                        Data period
                    </InputLabel>
                    <Select
                        labelId="period-select-label"
                        id="period-select"
                        value={semesters}
                        label="Select data period"
                        size="small"
                        renderValue={renderStyledPeriodValue}
                        onChange={handleSemestersChange}
                    >
                        <StyledMenuItem value="1">6 Months</StyledMenuItem>
                        <StyledMenuItem value="2">1 Year</StyledMenuItem>
                        <StyledMenuItem value="3">18 Months</StyledMenuItem>
                    </Select>
                </FormControl>
                <OverviewRefreshSection cachedAt={data && data.cachedAt} setRefresh={setRefresh} />
            </Stack>
            {(dashboard?.templates || []).map((template, templateIndex) => {
                return (
                    <Stack key={template.id} mb={4}>
                        <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                            style={{ width: "100%" }}
                        >
                            <Stack direction="row" alignItems="center" spacing={1}>
                                <TemplateTitle
                                    text={template.title.value}
                                    templateIndex={templateIndex}
                                />
                                <KnowledgebaseIconLink
                                    name={template.knowledgeBaseName}
                                    markdownId={null}
                                />
                            </Stack>
                            <Stack direction="row" alignItems="center" spacing={2}>
                                <InvalidIntegrationButton
                                    dependencies={template.dependencies}
                                    isTeams={false}
                                />
                            </Stack>
                        </Stack>
                        <Template
                            viewType="exec"
                            onEditMode={false}
                            toolsScrapping={[]}
                            template={template}
                            templateIndex={templateIndex}
                            defaultDateRange={dashboard.defaultDateRange}
                            setTemplateNoData={() => {}}
                            setEarliestFirstScrapped={() => {}}
                            setApiStatus={() => {}}
                        />
                    </Stack>
                );
            })}
            <Grid container spacing={2} justifyContent="center">
                <Grid item md={3.5} sx={{ width: "100%" }}>
                    <Stack direction="column">
                        <Stack direction="row" alignItems="center">
                            <SectionTitle>Investment</SectionTitle>
                            <IconButton
                                size="small"
                                onClick={() => setIsInvestmentFlipped(!isInvestmentFlipped)}
                                aria-label="View info on metrics"
                                color="secondary"
                            >
                                <InfoIcon />
                            </IconButton>
                        </Stack>
                        <InvestmentSection
                            fetchAction={fetchAction}
                            data={data}
                            expandedView={expandedView}
                            isFlipped={isInvestmentFlipped}
                        />
                    </Stack>
                </Grid>
                <Grid item md={3.5} sx={{ width: "100%" }}>
                    <Stack direction="column">
                        <Stack direction="row" alignItems="center">
                            <SectionTitle>Delivery</SectionTitle>
                            <IconButton
                                size="small"
                                onClick={() => setIsDeliveryFlipped(!isDeliveryFlipped)}
                                aria-label="View info on metrics"
                                color="secondary"
                            >
                                <InfoIcon />
                            </IconButton>
                        </Stack>
                        <Stack spacing={2}>
                            <DeliverySectionOutput
                                fetchAction={fetchAction}
                                data={data}
                                expandedView={expandedView}
                                isFlipped={isDeliveryFlipped}
                                setIsFlipped={setIsDeliveryFlipped}
                            />
                            <DeliverySectionEfficacy
                                fetchAction={fetchAction}
                                data={data}
                                expandedView={expandedView}
                                isFlipped={isDeliveryFlipped}
                                setIsFlipped={setIsDeliveryFlipped}
                            />
                        </Stack>
                    </Stack>
                </Grid>
                <Grid item md={5} sx={{ width: "100%" }}>
                    <Stack direction="column">
                        <Stack direction="row" alignItems="center" justifyContent="flex-start">
                            <SectionTitle>Credit Expenditure</SectionTitle>
                            <IconButton
                                size="small"
                                onClick={() => setIsExpenditureFlipped(!isExpenditureFlipped)}
                                aria-label="View info on metrics"
                                color="secondary"
                            >
                                <InfoIcon />
                            </IconButton>
                        </Stack>
                        <Stack spacing={2}>
                            <DevCost
                                fetchAction={fetchAction}
                                data={data}
                                expandedView={expandedView}
                                isFlipped={isExpenditureFlipped}
                                setIsFlipped={setIsExpenditureFlipped}
                            />
                            <CodeCost
                                fetchAction={fetchAction}
                                data={data}
                                expandedView={expandedView}
                                isFlipped={isExpenditureFlipped}
                                setIsFlipped={setIsExpenditureFlipped}
                            />
                        </Stack>
                    </Stack>
                </Grid>
            </Grid>
        </>
    );
};

export default OverviewMain;
